import { ActionIcon, Group, Indicator, Paper, Tooltip } from "@mantine/core";
import { nodeWidth } from "../../flowSettings";
import EventSelector from "./EventSelector";
import DelaySelector from "./DelaySelector";
import { getEvents } from "./events";
import { useFlowStore } from "../../../../../../../stores/WorkflowStore";
import { useMemo, useState } from "react";
import { useModelsStore } from "../../../../../../../stores/ConfigStore";
import EventFilter from "./EventFilter";
import { IconFilterCog } from "@tabler/icons-react";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import Linker from "./Linker";

const EventNode = ({ data, id, nodeDetail, xPos, yPos }) => {
  const lang = useLanguage((s) => s.language);
  const nodes = useFlowStore((s) => s.nodes);
  const linking = useFlowStore((s) => s.linking);
  const nodeSource = useMemo(
    () => nodes.find((x) => x.id === data?.source),
    [data?.source, nodes]
  );
  const models = useModelsStore((s) => s.model_emails);
  const workflowProfile = useFlowStore((s) => s.profile);

  const sourceModel = useMemo(() => {
    return models.find((x) => x.id === nodeSource?.data?.model_id);
  }, [models, nodeSource?.data?.model_id]);

  const messagesAreOnlyChildren = useMemo(() => {
    if (workflowProfile?.created_on) {
      return false;
    }
    let parentIsInvitation =
      nodeSource?.data?.actionType === "linkedin_invitation";

    if (parentIsInvitation) {
      let children = nodes?.filter((x) => x.data.source === id);
      if (children?.find((x) => x?.data?.actionType !== "linkedin_message")) {
        return false;
      }
      return true;
    }

    return false;
  }, [id, nodeSource?.data?.actionType, nodes, workflowProfile?.created_on]);

  const goodEvent = useMemo(() => {
    const correctEvents = getEvents(
      nodeSource?.data?.actionType,
      !!workflowProfile?.auto_remove_responders,
      messagesAreOnlyChildren
    );
    if (data?.event_type === "link_click") {
      return sourceModel?.links?.length > 0;
    }
    if (correctEvents.find((x) => x.value === data?.event_type)) {
      return true;
    }
    if (nodeSource?.data?.actionType === "call") return true;
    return false;
  }, [
    data?.event_type,
    messagesAreOnlyChildren,
    nodeSource?.data?.actionType,
    sourceModel?.links?.length,
    workflowProfile?.auto_remove_responders,
  ]);

  const [showFilter, setShowFilter] = useState(!!data?.filter);
  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  return (
    <Paper
      w={nodeWidth}
      withBorder
      p={"xs"}
      shadow={"sm"}
      className="action-node event"
      pos={"relative"}
      style={{
        borderColor: data?.error ? "red" : nodeDetail.color,
        borderWidth: data?.error ? 2 : 1,
        cursor: "default",
      }}
      radius={"md"}
    >
      <Group wrap="nowrap" align="start" gap={4}>
        <EventSelector
          event_type={data?.event_type}
          event_name={data?.name}
          goodEvent={goodEvent}
          id={id}
          which_links_clicked={data?.which_links_clicked}
          nodeSource={nodeSource}
          sourceModel={sourceModel}
          messagesAreOnlyChildren={messagesAreOnlyChildren}
          disabled={data?.number_contacts > 0}
        />
        <Tooltip label={lang.campaign.selectors.event.use_filter}>
          <Indicator
            offset={2}
            color="red"
            position="top-end"
            withBorder
            disabled={!data?.filter || data?.filter?.values?.length === 0}
            styles={{
              indicator: {
                width: 12,
                height: 12,
              },
            }}
            mt={19}
          >
            <ActionIcon
              onClick={handleShowFilter}
              variant={showFilter ? "light" : "default"}
              size={"md"}
              h={30}
            >
              <IconFilterCog size={16} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Group>

      {showFilter && (
        <EventFilter
          filter={data?.filter}
          id={id}
          disabled={data?.number_contacts > 0}
        />
      )}

      <DelaySelector
        id={id}
        when_minutes={data?.when_minutes}
        event_type={data?.event_type}
      />

      {!linking && <Linker id={id} xPos={xPos} yPos={yPos} />}
    </Paper>
  );
};

export default EventNode;
